import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ASSIGNMENT_ACTIONS, ASSIGNMENT_DIALOG_ACTIONS } from '../actions';
import { AssignStudyService } from '@submissions/v1/services/assign-study.service';
import { HttpParams } from '@angular/common/http';
import { Observable, of, tap } from 'rxjs';
import { SubmitAssignment } from '@data-access/assignment';
import { MatDialog } from '@angular/material/dialog';

@Injectable()
export class AssignmentEffects {
  private readonly _actions$ = inject(Actions); 
  private readonly _assignStudyService = inject(AssignStudyService);  
  private readonly _matDialog = inject(MatDialog);  
  
  searchEntity$ = createEffect(() => {
    const handleSearch = (search$: Observable<{ id: string }>) =>
      search$.pipe(
        map((result) => ASSIGNMENT_ACTIONS.searchEntitySuccess({ result })),
        catchError(() => of(ASSIGNMENT_ACTIONS.searchEntityFailure())),
      );

    return this._actions$.pipe(
      ofType(ASSIGNMENT_ACTIONS.searchEntity),
      switchMap(({ entity, query }) => {
        switch (entity) {
          case 'study':
            return handleSearch(
              this._assignStudyService.searchStudyAccess(
                new HttpParams().set('query', query),
              ),
            );
          case 'investigator':
            return handleSearch(this._assignStudyService.searchLegacyId(query));
          case 'site':
            return handleSearch(
              this._assignStudyService.searchSiteId(query as unknown as number),
            );
          default:
            return of(ASSIGNMENT_ACTIONS.searchEntityFailure());
        }
      }),
    );
  });

  submitAssignment$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ASSIGNMENT_ACTIONS.submitAssignment),
      switchMap(({ props }) => this._fetchAssignmentAction(props)),
    ),
  );

  /**
   * @DIALOG ACTIONS
   * */
  openDialog$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(ASSIGNMENT_DIALOG_ACTIONS.openDialog),
        tap(({ component, data }) => {
          if (!component()) {
            return;
          }

          this._matDialog.open(component(), {
            data,
          });
        }),
      ),
    {
      dispatch: false,
    },
  );

  private _fetchAssignmentAction(props: SubmitAssignment) {
    if (props.entity === 'investigator') {
      return this._assignStudyService
        .updateInvestigator(props.targetType, props.targetId, { ...props.body })
        .pipe(
          switchMap(() => {
            return [ASSIGNMENT_ACTIONS.submitAssignmentSuccess()];
          }),
          catchError((error: Error) =>
            of(
              ASSIGNMENT_ACTIONS.submitAssignmentFailure({
                error: error.message,
              }),
            ),
          ),
        );
    }
    if (
      props.entity === 'study' &&
      Object.hasOwn(props.body, 'reason_for_update')
    ) {
      const { title, reason_for_update } = props.body;
      return this._assignStudyService
        .updateStudyTitle(props.studyId, { title, reason_for_update })
        .pipe(
          switchMap(() => {
            return [ASSIGNMENT_ACTIONS.submitAssignmentSuccess()];
          }),
          catchError((error: Error) =>
            of(
              ASSIGNMENT_ACTIONS.submitAssignmentFailure({
                error: error.message,
              }),
            ),
          ),
        );
    }
    return this._assignStudyService
      .updateEntity(props.targetType, props.targetId, { ...props.body })
      .pipe(
        switchMap(() => {
          return [ASSIGNMENT_ACTIONS.submitAssignmentSuccess()];
        }),
        catchError((error: Error) =>
          of(
            ASSIGNMENT_ACTIONS.submitAssignmentFailure({
              error: error.message,
            }),
          ),
        ),
      );
  }
}
